// src/theme/cde.ts
import { theme } from 'antd';

export const ubuntuTheme = {
  // 使用浅色算法
  algorithm: theme.defaultAlgorithm,

  // 全局 Design Token
  token: {
    // 主色 – 亮蓝色，像素风常见
    colorPrimary: '#00A3FF',
    colorPrimaryHover: '#33B5FF',
    colorPrimaryActive: '#0088CC',
    colorPrimaryBg: '#E6F4FF',

    // 功能色 – 高饱和亮色
    colorSuccess: '#00CC44',
    colorWarning: '#FFAA00',
    colorError: '#FF2244',
    colorInfo: '#00A3FF',

    // 背景色 – 纯白与浅灰，干净利落
    colorBgBase: '#FFFFFF',
    colorBgContainer: '#F5F5F5',
    colorBgElevated: '#FAFAFA',
    colorBgLayout: '#E8E8E8',
    colorBgSpotlight: '#222222',   // 用于 Tooltip 等

    // 文字色 – 纯黑与深灰
    colorTextBase: '#000000',
    colorTextSecondary: '#333333',
    colorTextTertiary: '#666666',
    colorTextQuaternary: '#999999',

    // 边框 – 纯黑色，粗线
    colorBorder: '#000000',
    colorBorderSecondary: '#444444',

    // 无圆角（像素风必备）
    borderRadius: 0,
    borderRadiusSM: 0,
    borderRadiusLG: 0,
    borderRadiusXS: 0,
    borderRadiusOuter: 0,

    // 像素字体（需自行加载 "Press Start 2P"）
    fontFamily: "'Press Start 2P', 'Courier New', 'IBM Plex Mono', monospace",
    fontFamilyCode: "'Press Start 2P', 'Courier New', monospace",

    // 全局阴影 – 像素块偏移（部分组件会单独覆盖）
    boxShadow: '4px 4px 0 #000000',
    boxShadowSecondary: '2px 2px 0 #000000',

    // 控制组件尺寸（略为放大，更适合像素字体）
    controlHeight: 40,
    controlHeightSM: 32,
    controlHeightLG: 48,
    controlHeightXS: 28,

    // 间距
    padding: 20,
    paddingSM: 12,
    paddingLG: 24,
    paddingXS: 8,
    margin: 20,
    marginSM: 12,
    marginLG: 24,
    marginXS: 8,

    // 粗边框（像素特征）
    lineWidth: 4,
    lineWidthBold: 6,

    // 禁用动效（像素风通常无过渡）
    motion: false,
    motionUnit: 0,
    motionBase: 0,
  },

  // 组件级定制
  components: {
    // ========== 通用组件 ==========
    Button: {
      borderRadius: 0,
      // 按钮主色阴影 – 像素块外发光效果（利用 box-shadow）
      primaryShadow: '4px 4px 0 #000000',
      defaultShadow: '4px 4px 0 #000000',
      defaultBorderColor: '#000000',
      defaultHoverBorderColor: '#000000',
      defaultHoverColor: '#000000',
      defaultHoverBg: '#E5E5E5',
      contentFontSize: 16,
      contentFontSizeLG: 18,
      contentFontSizeSM: 14,
      controlHeight: 40,
      controlHeightLG: 48,
      controlHeightSM: 32,
      borderColor: '#000000',
      borderWidth: 4,
      fontWeight: 'bold',
      colorPrimaryHover: '#33B5FF',
      colorPrimaryActive: '#0088CC',
      // 为了像素感，去掉内阴影，仅保留外阴影
    },

    Layout: {
      headerBg: '#222222',        // 深色标题栏，对比强烈
      headerColor: '#00FFAA',     // 亮绿色文字
      headerPadding: '0 20px',
      bodyBg: '#E8E8E8',
      footerBg: '#CCCCCC',
      footerPadding: '8px 20px',
      triggerBg: '#DDDDDD',
      triggerColor: '#000000',
      triggerHeight: 48,
      zeroTriggerHeight: 48,
    },

    Menu: {
      colorItemBg: 'transparent',
      colorSubItemBg: 'rgba(0,0,0,0.1)',
      colorItemText: '#000000',
      colorItemTextSelected: '#000000',
      colorItemBgSelected: '#BBBBBB',
      colorItemBgActive: '#CCCCCC',
      radiusItem: 0,
      controlHeightLG: 40,
      itemMarginInline: 0,
      borderColor: '#000000',
      borderWidth: 4,
    },

    Breadcrumb: {
      colorText: '#333333',
      colorTextSecondary: '#000000',
      lastItemColor: '#000000',
      separatorColor: '#666666',
    },

    Pagination: {
      borderRadius: 0,
      colorPrimary: '#000000',
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      borderWidth: 4,
    },

    Steps: {
      colorPrimary: '#000000',
      colorText: '#333333',
      colorTextSecondary: '#666666',
      colorBgContainer: '#FFFFFF',
      borderRadius: 0,
    },

    Tabs: {
      colorPrimary: '#000000',
      colorText: '#333333',
      colorTextActive: '#000000',
      colorTextHover: '#00A3FF',
      colorBorder: '#000000',
      cardBg: '#F5F5F5',
      cardGutter: 4,
      horizontalMargin: '0 0 8px 0',
      horizontalItemGutter: 20,
      borderWidth: 4,
    },

    // ========== 数据录入 ==========
    Input: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      colorPrimaryHover: '#00A3FF',
      borderRadius: 0,
      controlHeight: 40,
      controlHeightLG: 48,
      controlHeightSM: 32,
      activeBg: '#FFFFFF',
      activeBorderColor: '#000000',
      hoverBorderColor: '#000000',
      borderWidth: 4,
    },

    Select: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      borderRadius: 0,
      controlHeight: 40,
      controlHeightLG: 48,
      controlHeightSM: 32,
      optionSelectedBg: '#BBBBBB',
      optionActiveBg: '#DDDDDD',
      borderWidth: 4,
    },

    Cascader: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      borderRadius: 0,
      controlHeight: 40,
      controlHeightLG: 48,
      controlHeightSM: 32,
      borderWidth: 4,
    },

    DatePicker: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      borderRadius: 0,
      controlHeight: 40,
      controlHeightLG: 48,
      controlHeightSM: 32,
      cellHeight: 32,
      cellWidth: 40,
      borderWidth: 4,
    },

    TimePicker: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      borderRadius: 0,
      controlHeight: 40,
      controlHeightLG: 48,
      controlHeightSM: 32,
      borderWidth: 4,
    },

    InputNumber: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      borderRadius: 0,
      controlHeight: 40,
      controlHeightLG: 48,
      controlHeightSM: 32,
      borderWidth: 4,
    },

    Checkbox: {
      colorPrimary: '#000000',
      colorBorder: '#000000',
      borderRadius: 0,
      controlInteractiveSize: 20,
    },

    Radio: {
      colorPrimary: '#000000',
      colorBorder: '#000000',
      radioSize: 20,
      dotSize: 10,
    },

    Switch: {
      colorPrimary: '#000000',
      colorPrimaryHover: '#333333',
      handleBg: '#FFFFFF',
      handleShadow: '2px 2px 0 #000000',
      trackHeight: 28,
      trackMinWidth: 52,
      innerMinMargin: 6,
    },

    Slider: {
      colorPrimary: '#000000',
      colorPrimaryHover: '#333333',
      colorPrimaryBorder: '#000000',
      colorPrimaryBorderHover: '#333333',
      trackBg: '#000000',
      trackHoverBg: '#333333',
      handleColor: '#000000',
      handleSize: 16,
      handleSizeHover: 18,
      dotSize: 8,
    },

    Rate: {
      colorPrimary: '#FFAA00',
      starSize: 24,
      starBg: '#999999',
    },

    Upload: {
      colorPrimary: '#000000',
      colorBorder: '#000000',
      borderRadius: 0,
      borderWidth: 4,
    },

    Form: {
      labelColor: '#000000',
      labelFontSize: 16,
      labelHeight: 40,
      itemMarginBottom: 20,
    },

    Mentions: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      borderRadius: 0,
      controlHeight: 40,
      controlHeightLG: 48,
      controlHeightSM: 32,
      borderWidth: 4,
    },

    AutoComplete: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      borderRadius: 0,
      controlHeight: 40,
      controlHeightLG: 48,
      controlHeightSM: 32,
      borderWidth: 4,
    },

    TreeSelect: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      borderRadius: 0,
      controlHeight: 40,
      controlHeightLG: 48,
      controlHeightSM: 32,
      borderWidth: 4,
    },

    ColorPicker: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      borderRadius: 0,
      controlHeight: 40,
      borderWidth: 4,
    },

    Transfer: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      borderRadius: 0,
      listWidth: 220,
      listHeight: 280,
      borderWidth: 4,
    },

    // ========== 数据展示 ==========
    Table: {
      borderColor: '#000000',
      headerBg: '#CCCCCC',
      headerColor: '#000000',
      headerSortActiveBg: '#BBBBBB',
      headerSortHoverBg: '#BBBBBB',
      rowHoverBg: 'rgba(0, 163, 255, 0.15)',
      rowSelectedBg: 'rgba(0, 163, 255, 0.25)',
      rowSelectedHoverBg: 'rgba(0, 163, 255, 0.35)',
      cellPaddingInline: 16,
      cellPaddingBlock: 12,
      borderRadius: 0,
      borderWidth: 4,
    },

    Card: {
      colorBgContainer: '#FFFFFF',
      colorBorderSecondary: '#000000',
      boxShadow: '4px 4px 0 #000000',
      borderRadiusLG: 0,
      padding: 20,
      paddingSM: 16,
      paddingLG: 24,
      headerBg: 'transparent',
      headerFontSize: 18,
      borderWidth: 4,
    },

    List: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      colorText: '#000000',
      colorTextSecondary: '#333333',
      padding: 20,
      paddingSM: 16,
      paddingLG: 24,
      metaMargin: 20,
    },

    Descriptions: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      colorText: '#000000',
      colorTextSecondary: '#333333',
      labelColor: '#333333',
      titleColor: '#000000',
      padding: 20,
      paddingSM: 16,
      paddingLG: 24,
      borderRadius: 0,
      borderWidth: 4,
    },

    Avatar: {
      colorBgContainer: '#888888',
      colorText: '#FFFFFF',
      borderRadius: 0,
      borderRadiusLG: 0,
      borderRadiusSM: 0,
      size: 48,
      sizeLG: 64,
      sizeSM: 32,
    },

    Badge: {
      borderRadius: 0,
      colorBgContainer: '#FF2244',
      colorText: '#FFFFFF',
      dotSize: 10,
      shadowSize: 0,
    },

    Tag: {
      colorBgContainer: '#E5E5E5',
      colorBorder: '#000000',
      colorText: '#000000',
      borderRadius: 0,
      defaultBg: '#E5E5E5',
      defaultColor: '#000000',
      borderWidth: 4,
    },

    Popover: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      borderRadius: 0,
      boxShadow: '4px 4px 0 #000000',
      padding: 16,
      borderWidth: 4,
    },

    Tooltip: {
      colorBgSpotlight: '#222222',
      colorTextLightSolid: '#00FFAA',
      borderRadius: 0,
      fontSize: 14,
      padding: 10,
    },

    Popconfirm: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      borderRadius: 0,
      boxShadow: '4px 4px 0 #000000',
      borderWidth: 4,
    },

    Dropdown: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      borderRadius: 0,
      boxShadow: '4px 4px 0 #000000',
      padding: 6,
      borderWidth: 4,
    },

    Tree: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      colorText: '#000000',
      colorTextSecondary: '#333333',
      nodeHoverColor: 'rgba(0, 163, 255, 0.1)',
      nodeSelectedColor: 'rgba(0, 163, 255, 0.2)',
      indentSize: 20,
      borderRadius: 0,
    },

    Calendar: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      colorText: '#000000',
      colorTextSecondary: '#333333',
      fullBg: '#E8E8E8',
      fullPanelBg: '#FFFFFF',
      itemActiveBg: 'rgba(0, 163, 255, 0.2)',
    },

    Statistic: {
      colorBgContainer: '#FFFFFF',
      colorText: '#000000',
      colorTextSecondary: '#333333',
      titleFontSize: 16,
      contentFontSize: 28,
    },

    Comment: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      colorText: '#000000',
      colorTextSecondary: '#333333',
      padding: 20,
    },

    Carousel: {
      colorBgContainer: '#888888',
      colorText: '#FFFFFF',
      dotHeight: 6,
      dotWidth: 24,
    },

    Collapse: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      colorText: '#000000',
      colorTextSecondary: '#333333',
      headerBg: '#E5E5E5',
      borderRadius: 0,
      padding: 16,
      borderWidth: 4,
    },

    Timeline: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      colorText: '#000000',
      colorTextSecondary: '#333333',
      dotBg: '#00A3FF',
      tailColor: '#000000',
    },

    // ========== 反馈组件 ==========
    Alert: {
      borderRadius: 0,
      colorText: '#000000',
      colorTextSecondary: '#333333',
      padding: 16,
      paddingSM: 12,
      borderWidth: 4,
    },

    Notification: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      borderRadius: 0,
      boxShadow: '4px 4px 0 #000000',
      padding: 20,
      paddingLG: 24,
      borderWidth: 4,
    },

    Message: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      borderRadius: 0,
      boxShadow: '4px 4px 0 #000000',
      padding: 16,
      borderWidth: 4,
    },

    Modal: {
      borderRadiusLG: 0,
      boxShadow: '6px 6px 0 #000000',
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      headerBg: '#222222',        // 深色标题栏
      headerColor: '#00FFAA',
      titleColor: '#00FFAA',
      titleFontSize: 20,
      padding: 24,
      paddingLG: 32,
      paddingMD: 20,
      paddingXS: 16,
      borderWidth: 4,
    },

    Drawer: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      borderRadiusLG: 0,
      boxShadow: '4px 4px 0 #000000',
      padding: 24,
      titleColor: '#000000',
      titleFontSize: 20,
      borderWidth: 4,
    },

    Progress: {
      colorBgContainer: '#999999',
      colorText: '#333333',
      colorTextSecondary: '#666666',
      defaultColor: '#00A3FF',
      remainingColor: '#999999',
      borderRadius: 0,
      lineWidth: 12,
    },

    Result: {
      colorBgContainer: '#FFFFFF',
      colorText: '#000000',
      colorTextSecondary: '#333333',
      titleFontSize: 28,
      subtitleFontSize: 18,
      iconSize: 56,
    },

    Skeleton: {
      colorBgContainer: '#999999',
      colorText: '#DDDDDD',
      borderRadius: 0,
    },

    Empty: {
      colorBgContainer: 'transparent',
      colorText: '#333333',
      colorTextSecondary: '#666666',
    },

    Spin: {
      colorPrimary: '#00A3FF',
      colorBgContainer: 'rgba(232, 232, 232, 0.6)',
    },

    // ========== 其他 ==========
    Anchor: {
      colorBgContainer: 'transparent',
      colorBorder: '#000000',
      colorText: '#333333',
      colorTextActive: '#00A3FF',
      colorTextHover: '#00A3FF',
    },

    BackTop: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      boxShadow: '4px 4px 0 #000000',
      borderWidth: 4,
    },

    App: {
      colorBgContainer: '#E8E8E8',
      colorText: '#000000',
    },

    QRCode: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      borderRadius: 0,
      borderWidth: 4,
    },

    Tour: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#000000',
      borderRadius: 0,
      boxShadow: '4px 4px 0 #000000',
      borderWidth: 4,
    },
  },
};